import { CampaignPhase, SocialCampaignDTO } from 'models/socialCampaign'
import { SystemMessage, SystemType } from 'components/common/SystemMessage'
import { campaignDayOf, countCampaignPosts, formatCampaignDay } from 'utils/socialCampaign/campaignStatus'

import { CampaignFlash } from './CampaignFlash.context'
import { CloseOutlined } from '@mui/icons-material'
import { FC } from 'react'
import { MUIButton } from 'components/common/MUIButton'
import { TFunction } from 'i18next'
import styles from './CampaignMessages.module.sass'
import { useTranslation } from 'react-i18next'

const FLASH_VARIANT: Record<CampaignFlash['kind'], SystemType> = {
  launched: 'success',
  generated: 'info',
  not_publishable: 'warning',
  resumed: 'success',
  reopened: 'info',
  error: 'error',
}

/** A reason the backend added after this page was built reads as a generic one, said once. */
const reasonsText = (reasons: string[], t: TFunction) =>
  [...new Set(reasons.map((reason) => t(`flash.reason.${reason}`, { defaultValue: t('flash.reason.other') })))].join(', ')

const flashText = (flash: CampaignFlash, t: TFunction) => {
  switch (flash.kind) {
    case 'launched':
      return [
        t('flash.launched', { count: flash.scheduled }),
        ...(flash.waiting ? [t('flash.launched_waiting', { count: flash.waiting })] : []),
      ].join(' ')
    case 'generated':
      return t('flash.generated', { count: flash.count })
    case 'not_publishable':
      return [
        t('flash.not_publishable', { count: flash.posts.length }),
        flash.posts
          .map((post) => t('flash.not_publishable_post', { subject: post.subject, reasons: reasonsText(post.reasons, t) }))
          .join(' '),
      ].join(' ')
    case 'resumed':
      return [
        t('flash.resumed'),
        ...(flash.missed ? [t('flash.resumed_missed', { count: flash.missed })] : []),
      ].join(' ')
    case 'reopened':
      return t('flash.reopened')
    case 'error':
      return t('flash.error')
  }
}

interface Props {
  campaign: SocialCampaignDTO
  now: Date
  flash: CampaignFlash | null
  onDismissFlash: () => void
  onResume: () => void
  onReopen: () => void
  /** A resume or a reopening is on its way */
  busy: boolean
}

/**
 * The messages under the header (prototype v3 `campaignHtml` msgs): the flash after an action,
 * then what a paused campaign holds (Resume), or that an ended one ended (Reopen). The flash's
 * dismiss sits at its end, outside the live region, so it is not read with the message.
 */
export const CampaignMessages: FC<Props> = ({ campaign, now, flash, onDismissFlash, onResume, onReopen, busy }) => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const paused = campaign.phase === CampaignPhase.PAUSED && campaign.pausedAt !== null
  const ended = campaign.phase === CampaignPhase.ENDED

  if (!flash && !paused && !ended) return null

  const counts = countCampaignPosts(campaign, now)

  return (
    <div className={styles.messages}>

      {flash &&
        <div className={styles.flash}>

          <SystemMessage
            variant={FLASH_VARIANT[flash.kind]}
            role={flash.kind === 'error' ? 'alert' : 'status'}
            message={flashText(flash, t)}
            isRawContent
            sx={{ pr: '4.8rem' }}
          />

          <span className={styles.dismiss}>
            <MUIButton type="secondaryNoBorder" size="sm" iconOnly aria-label={t('flash.dismiss')} onClick={onDismissFlash}>
              <CloseOutlined />
            </MUIButton>
          </span>

        </div>
      }

      {paused &&
        <SystemMessage
          variant="info"
          role="status"
          title={t('paused.title', { date: formatCampaignDay(campaignDayOf(campaign.pausedAt as string, campaign.timezone), i18n.language) })}
          message={[
            t('paused.held', { count: counts.held }),
            ...(counts.missedDuringPause ? [t('paused.missed', { count: counts.missedDuringPause })] : []),
            t('paused.nothing_out'),
          ].join(' ')}
          isRawContent
          actions={<MUIButton type="defaultPrimary" size="sm" disabled={busy} onClick={onResume}>{t('paused.resume')}</MUIButton>}
        />
      }

      {ended &&
        <SystemMessage
          variant="system"
          role="status"
          title={t('ended.title')}
          message={t('ended.body')}
          isRawContent
          actions={<MUIButton type="secondaryBorder" size="sm" disabled={busy} onClick={onReopen}>{t('ended.reopen')}</MUIButton>}
        />
      }

    </div>
  )
}
